"use client";

import { useCallback } from "react";
import { FiCheck, FiRefreshCw, FiX } from "react-icons/fi";

import AIBadge from "@/components/ui/ai-badge";
import Badge from "@/components/ui/badge";
import Button from "@/components/ui/button";
import Tooltip from "@/components/ui/tooltip";

export interface AIToolbarProps {
  /** Whether generation is in progress */
  isGenerating: boolean;
  /** Whether there's an error */
  hasError: boolean;
  /** Whether there's generated text to accept */
  hasGeneratedText: boolean;
  /** Number of feedback revisions */
  feedbackHistoryCount: number;
  /** Current feedback text */
  feedback: string;
  /** Callback when feedback changes */
  onFeedbackChange: (feedback: string) => void;
  /** Callback when accept is clicked */
  onAccept: () => void;
  /** Callback when retry is clicked */
  onRetry: () => void;
  /** Callback when regenerate is clicked (with feedback) */
  onRegenerate: () => void;
  /** Callback when cancel is clicked */
  onCancel: () => void;
  /** Optional badge to display */
  badge?: { label: string; variant: "success" | "info" | "error" } | null;
  /** Optional additional info text */
  infoText?: string;
  /** Optional placeholder for feedback input */
  feedbackPlaceholder?: string;
}

export function AIToolbar({
  isGenerating,
  hasError,
  hasGeneratedText,
  feedbackHistoryCount,
  feedback,
  onFeedbackChange,
  onAccept,
  onRetry,
  onRegenerate,
  onCancel,
  badge,
  infoText,
  feedbackPlaceholder = "Add feedback and press Enter to regenerate...",
}: AIToolbarProps) {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (feedback.trim()) {
          onRegenerate();
        } else {
          onRetry();
        }
      }
    },
    [feedback, onRegenerate, onRetry],
  );

  return (
    <div className="border-t border-border bg-background-secondary/50">
      <div className="flex items-center justify-between px-2 py-1.5">
        <div className="flex items-center gap-1.5">
          <AIBadge size={14} />
          <span className="text-caption-strong text-primary">
            AI Suggestion
          </span>
          {isGenerating && (
            <span className="text-caption text-foreground-muted">
              {infoText || "generating..."}
            </span>
          )}
          {hasError && (
            <span className="text-caption text-status-error-text">failed</span>
          )}
          {!isGenerating && !hasError && infoText && (
            <span className="text-caption text-foreground-secondary">
              · {infoText}
            </span>
          )}
          {badge && (
            <Badge
              variant={
                badge.variant === "info"
                  ? "default"
                  : badge.variant === "success"
                    ? "success"
                    : "error"
              }
            >
              {badge.label}
            </Badge>
          )}
          {feedbackHistoryCount > 0 && !isGenerating && (
            <span className="text-caption text-foreground-muted">
              · {feedbackHistoryCount} revision
              {feedbackHistoryCount > 1 ? (
                <span>{"s"}</span>
              ) : (
                <span>{""}</span>
              )}
            </span>
          )}
        </div>

        <div className="flex items-center gap-0.5">
          {!isGenerating && hasGeneratedText && !hasError && (
            <Tooltip content="Accept">
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={onAccept}
                className="text-primary hover:bg-hover hover:text-primary"
              >
                <FiCheck className="h-4 w-4" />
              </Button>
            </Tooltip>
          )}
          {!isGenerating && (
            <Tooltip content="Regenerate">
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={onRetry}
                className="text-foreground-secondary hover:text-foreground"
              >
                <FiRefreshCw className="h-4 w-4" />
              </Button>
            </Tooltip>
          )}
          <Tooltip content="Cancel">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={onCancel}
              className="text-foreground-secondary hover:text-status-error-text"
            >
              <FiX className="h-4 w-4" />
            </Button>
          </Tooltip>
        </div>
      </div>

      {!isGenerating && (
        <input
          type="text"
          placeholder={feedbackPlaceholder}
          value={feedback}
          onChange={(e) => onFeedbackChange(e.target.value)}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          style={{ outline: "none", boxShadow: "none" }}
          className="w-full appearance-none border-0 border-t border-border bg-transparent px-3 py-2 text-body text-foreground-secondary placeholder:text-foreground-muted"
        />
      )}
    </div>
  );
}
